import { Controller } from 'framework/core';
import { Singleton } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { DomHelper } from '../../helpers/dom.helper';
import { WCAGHelper } from '../../helpers/wcag.helper';
import { AppService } from '../app/app.service';
import { AddToCartResult } from './cart.model';
import { CartSelectors } from './cart.selectors';
import { CartService } from './cart.service';

interface CartState {
  isShow: boolean;
  isInitialized: boolean;
  isRendering: boolean;
}

const originalDialogShowModal =
  typeof HTMLDialogElement !== 'undefined' ? HTMLDialogElement.prototype.showModal : null;
let isDialogShowModalPatched = false;

/**
 * Cart controller managing UI interactions, modal display, and cart operations
 *
 * This controller provides extensive customization capabilities for Technical Support teams
 * through protected extension points. All timing, selectors, translations, and behavior
 * can be customized per-shop without modifying core code.
 *
 * ## Key Extension Points
 *
 * ### Button State Management
 * - `updateButtonState()` - Customize button UI updates (text, disabled state, styling)
 * - `getButtonStateTranslations()` - Override button text for different locales
 * - `getButtonSuccessResetDelay()` - Control how long "Added!" shows (default: 500ms)
 * - `getButtonFailureResetDelay()` - Control error state duration (default: 300ms)
 *
 * ### Cart Opening Behavior
 * - `shouldOpenCartAfterAdd()` - Control when cart opens after add-to-cart
 * - `openCart()` - Customize cart opening logic and animations
 * - `closeCart()` - Customize cart closing and cleanup
 *
 * ### Theme Integration
 * - `getCartEnabledBodyClass()` - Override body class for cart enabled state
 * - `getModalId()` - Customize modal element ID
 * - `getModalOpenBodyClass()` - Override body class when modal is open
 * - `getButtonTextSelectors()` - Add custom theme button selectors
 *
 * ### Accessibility & Focus Management
 * - `setupWCAGAccessibility()` - Extend WCAG compliance features
 * - `saveFocusElementBeforeCart()` - Customize focus saving logic
 * - `restoreFocusAfterCart()` - Customize focus restoration logic
 *
 * ## Common Customization Patterns
 *
 * @example
 * ```javascript
 * // EXAMPLE 1: Multi-language shop with custom translations
 * window.boostWidgetIntegration.extend('CartController', (CartController) => {
 *   return class MultilingualCartController extends CartController {
 *     getButtonStateTranslations() {
 *       const locale = window.Shopify.locale || 'en';
 *       const translations = {
 *         de: {
 *           adding: 'Wird hinzugefügt...',
 *           added: 'Hinzugefügt!',
 *           failed: 'Fehler!',
 *           default: 'In den Warenkorb'
 *         },
 *         fr: {
 *           adding: 'Ajout...',
 *           added: 'Ajouté!',
 *           failed: 'Échec!',
 *           default: 'Ajouter au panier'
 *         }
 *       };
 *       return translations[locale] || super.getButtonStateTranslations();
 *     }
 *   };
 * });
 *
 * // EXAMPLE 2: Premium shop with faster button feedback and custom styling
 * window.boostWidgetIntegration.extend('CartController', (CartController) => {
 *   return class PremiumCartController extends CartController {
 *     getButtonSuccessResetDelay() {
 *       return 300; // Faster than default 500ms
 *     }
 *
 *     updateButtonState(button, state, customText) {
 *       super.updateButtonState(button, state, customText);
 *
 *       // Add premium loading animation
 *       const icon = button.querySelector('.icon');
 *       if (state === 'adding') {
 *         icon?.classList.add('spinner-animate');
 *       } else {
 *         icon?.classList.remove('spinner-animate');
 *       }
 *     }
 *   };
 * });
 *
 * // EXAMPLE 3: Shop that doesn't open cart for gift cards
 * window.boostWidgetIntegration.extend('CartController', (CartController) => {
 *   return class GiftCardCartController extends CartController {
 *     shouldOpenCartAfterAdd(params, result) {
 *       // Don't open cart for gift card purchases
 *       if (params?.productType === 'gift-card') {
 *         return false;
 *       }
 *       return super.shouldOpenCartAfterAdd(params, result);
 *     }
 *   };
 * });
 *
 * // EXAMPLE 4: Shop with custom analytics tracking
 * window.boostWidgetIntegration.extend('CartController', (CartController) => {
 *   return class AnalyticsCartController extends CartController {
 *     async addToCart(params, button, shouldOpenCart, callback) {
 *       // Track before add
 *       this.trackAddToCartStart(params);
 *
 *       try {
 *         const result = await super.addToCart(params, button, shouldOpenCart, callback);
 *         this.trackAddToCartSuccess(params);
 *         return result;
 *       } catch (error) {
 *         this.trackAddToCartFailure(params, error);
 *         throw error;
 *       }
 *     }
 *
 *     trackAddToCartStart(params) {
 *       window.dataLayer?.push({
 *         event: 'add_to_cart_start',
 *         ecommerce: { items: [params] }
 *       });
 *     }
 *
 *     trackAddToCartSuccess(params) {
 *       window.dataLayer?.push({
 *         event: 'add_to_cart',
 *         ecommerce: { items: [params] }
 *       });
 *     }
 *
 *     trackAddToCartFailure(params, error) {
 *       window.dataLayer?.push({
 *         event: 'add_to_cart_error',
 *         error: error.message,
 *         params
 *       });
 *     }
 *   };
 * });
 *
 * // EXAMPLE 5: Custom theme with different selectors
 * window.boostWidgetIntegration.extend('CartController', (CartController) => {
 *   return class CustomThemeCartController extends CartController {
 *     getButtonTextSelectors() {
 *       return [
 *         ...super.getButtonTextSelectors(),
 *         '.custom-theme-button',
 *         '.my-add-to-cart-btn'
 *       ];
 *     }
 *
 *     getModalId() {
 *       return 'my-custom-cart-modal';
 *     }
 *
 *     getCartEnabledBodyClass() {
 *       return 'custom-cart-enabled';
 *     }
 *   };
 * });
 * ```
 */
@Singleton()
export class CartController extends Controller {
  protected trapFocusHandlers = new WeakMap<Element, EventListener>(); // Store event handlers for focus trap
  protected closeButtonHandler: (() => void) | null = null; // Store close button handler
  protected viewCartHandler: (() => void) | null = null; // Store view cart handler
  protected checkoutHandler: (() => void) | null = null; // Store checkout handler
  protected outsideClickHandler: ((e: Event) => void) | null = null; // Store outside click handler
  protected originalButtonTexts = new WeakMap<Element, string>(); // Store original button texts
  protected readonly openCartEventHandler = (): void => {
    this.openCart();
  };
  protected readonly closeCartEventHandler = (): void => {
    this.closeCart();
  };
  protected readonly rerenderCartEventHandler = (): void => {
    void this.render();
  };
  protected topLayerMonitoringInterval: number | null = null;

  protected SUBMIT_KEYS = ['Enter', ' ', 'Spacebar'];

  protected state: Signal<CartState>;

  /**
   * Stores the element that had focus before mini cart was opened
   * Used for WCAG-compliant focus restoration when mini cart closes
   */
  focusedElementBeforeMiniCart: HTMLElement | null = null;

  constructor(
    protected cartService: CartService,
    protected cartSelectors: CartSelectors,
    protected appService: AppService,
    protected wcagHelper: WCAGHelper,
    protected domHelper: DomHelper
  ) {
    super();
    this.state = signal({
      isShow: false,
      isInitialized: false,
      isRendering: false,
    });
  }

  /**
   * Get translation text for cart UI elements
   * Override to customize translations for specific shops/locales
   *
   * @param key - Translation key
   * @param defaultValue - Fallback value if translation not found
   * @returns Translated text or default value
   * @example
   * ```typescript
   * protected getTranslation(key: string, defaultValue: string): string {
   *   const customKeys = { atcAddToCartBtnLabel: 'Custom Add to Bag' };
   *   return customKeys[key] || super.getTranslation(key, defaultValue);
   * }
   * ```
   */
  protected getTranslation(key: string, defaultValue: string): string {
    const translation = this.appService.translation;
    const productItem = translation?.productItem;
    const value = productItem?.[key];

    return typeof value === 'string' ? value : defaultValue;
  }

  /**
   * Get CSS class name for cart-enabled body
   * Override to use different class name for specific themes
   *
   * @returns CSS class name
   */
  protected getCartEnabledBodyClass(): string {
    return 'boost-sd__cart-enabled';
  }

  /**
   * Get modal ID for cart modal element
   * Override to use different modal ID for specific themes
   *
   * @returns Modal element ID
   */
  protected getModalId(): string {
    return 'boost-sd__modal-cart';
  }

  /**
   * Get CSS class name for modal-open body state
   * Override to use different class name for specific themes
   *
   * @returns CSS class name
   */
  protected getModalOpenBodyClass(): string {
    return 'modal-open-cart';
  }

  /**
   * Get button text selectors for cart buttons
   * Override to use different selectors for custom themes
   *
   * @returns Array of CSS selectors
   * @example
   * ```typescript
   * // Add custom theme button selector
   * protected getButtonTextSelectors(): string[] {
   *   return [...super.getButtonTextSelectors(), '.custom-theme-btn'];
   * }
   * ```
   */
  protected getButtonTextSelectors(): string[] {
    return ['.boost-sd__cta-button-text', '.boost-sd__btn-add-to-cart'];
  }

  /**
   * Update button state (text and disabled state)
   * Protected method - override to customize button state updates for specific shops
   *
   * @param button - Button element to update
   * @param state - Button state ('adding' | 'added' | 'failed' | 'default')
   * @param customText - Optional custom text override
   * @example
   * ```typescript
   * // Add loading spinner for premium UX
   * protected updateButtonState(button: HTMLElement, state: string, customText?: string) {
   *   super.updateButtonState(button, state, customText);
   *   if (state === 'adding') {
   *     button.querySelector('.icon')?.classList.add('spinner');
   *   } else {
   *     button.querySelector('.icon')?.classList.remove('spinner');
   *   }
   * }
   * ```
   */
  protected updateButtonState(
    button: HTMLElement,
    state: 'adding' | 'added' | 'failed' | 'default',
    customText?: string
  ): void {
    const ctaBtnText = button.querySelector(this.getButtonTextSelectors().join(', '));
    const element = ctaBtnText instanceof HTMLElement ? ctaBtnText : button;
    const isIconButton = element.classList.contains(this.getIconButtonClass());

    // Update text for non-icon buttons
    if (!isIconButton && element.textContent !== undefined) {
      // Save original text on first interaction (when not in default state)
      if (
        state !== 'default' &&
        !this.originalButtonTexts.has(element) &&
        element.textContent !== null
      ) {
        this.originalButtonTexts.set(element, element.textContent.trim());
      }

      if (customText) {
        element.textContent = customText;
      } else if (state === 'default') {
        // Restore original text instead of using generic default
        const originalText = this.originalButtonTexts.get(element);
        if (originalText !== undefined) {
          element.textContent = originalText;
        } else {
          // Fallback to translation default if no original text saved
          const translations = this.getButtonStateTranslations();
          element.textContent = translations.default;
        }
      } else {
        const translations = this.getButtonStateTranslations();
        element.textContent = translations[state] || translations.default;
      }
    }

    // Update disabled state
    if (state === 'adding') {
      element.setAttribute('disabled', 'true');
    } else if (state === 'added' || state === 'default') {
      element.removeAttribute('disabled');
    }
  }

  /**
   * Get button state translations
   * Override to customize button text for different states and locales
   *
   * @returns Object mapping button states to translated text
   * @example
   * ```typescript
   * // Customize for German locale
   * protected getButtonStateTranslations() {
   *   return {
   *     adding: 'Wird hinzugefügt...',
   *     added: 'Hinzugefügt!',
   *     failed: 'Fehler!',
   *     default: 'In den Warenkorb'
   *   };
   * }
   * ```
   */
  protected getButtonStateTranslations(): Record<string, string> {
    return {
      adding: this.getTranslation('atcAddingToCartBtnLabel', 'Adding...'),
      added: this.getTranslation('atcAddedToCartBtnLabel', 'Added!'),
      failed: this.getTranslation('atcFailedToCartBtnLabel', 'Failed!'),
      default: this.getTranslation('atcAddToCartBtnLabel', 'Add to Cart'),
    };
  }

  /**
   * Get icon button class name
   * Override for custom icon button styling
   *
   * @returns CSS class name
   */
  protected getIconButtonClass(): string {
    return 'boost-sd__button--icon';
  }

  /**
   * Get delay (ms) for resetting button text after successful add to cart
   * Override to customize button reset timing for specific shops
   *
   * @returns Delay in milliseconds (default: 500ms)
   * @example
   * ```typescript
   * // Faster button reset for premium shop experience
   * protected getButtonSuccessResetDelay(): number {
   *   return 300; // Faster than default 500ms
   * }
   * ```
   */
  protected getButtonSuccessResetDelay(): number {
    return 500;
  }

  /**
   * Get delay (ms) for resetting button text after failed add to cart
   * Override to customize error state timing for specific shops
   *
   * @returns Delay in milliseconds (default: 300ms)
   * @example
   * ```typescript
   * // Longer error display for better user awareness
   * protected getButtonFailureResetDelay(): number {
   *   return 1000; // Show error longer than default 300ms
   * }
   * ```
   */
  protected getButtonFailureResetDelay(): number {
    return 300;
  }

  /**
   * Get checkout URL path
   * Override to customize checkout URL for specific shops
   *
   * @returns Checkout URL path
   */
  protected getCheckoutUrl(): string {
    return '/checkout';
  }

  /**
   * Get cart page URL path
   * Override to customize cart page URL for specific shops
   *
   * @returns Cart page URL path
   */
  protected getCartPagePath(): string {
    return 'cart';
  }

  async connect(): Promise<void> {
    await super.connect();

    if (this.state.value.isInitialized) {
      return;
    }
    const { enableCart } = this.appService.themeSettings.cart;
    if (!enableCart) {
      return;
    }

    this.state.value.isInitialized = true;
    // Add class to body if cart is enabled to disable the default cart by css
    document.body.classList.add(this.getCartEnabledBodyClass());

    this.setupCartIcon();
    this.setupThemeCartDisabling();
    this.setupEventListeners();
  }

  /**
   * Setup theme cart override - simple pattern
   * Hide existing theme cart dialogs to show app cart instead
   */
  protected setupThemeCartDisabling(): void {
    // Setup global overrides for dialog methods
    this.setupGlobalDialogOverrides();

    // Hide existing theme cart dialogs once during initialization
    this.hideExistingThemeCartDialogs();

    // Setup periodic monitoring for dialogs in top-layer
    this.setupTopLayerMonitoring();
  }

  /**
   * Setup global overrides for HTMLDialogElement methods to prevent theme cart dialogs
   */
  protected setupGlobalDialogOverrides(): void {
    if (
      typeof HTMLDialogElement === 'undefined' ||
      !originalDialogShowModal ||
      isDialogShowModalPatched
    ) {
      return;
    }

    // Override showModal globally (most common method for cart dialogs)
    HTMLDialogElement.prototype.showModal = function () {
      if (this.classList.contains('cart-drawer__dialog')) {
        this.style.setProperty('display', 'none', 'important');
        return;
      }

      return originalDialogShowModal.call(this);
    };

    isDialogShowModalPatched = true;
  }

  protected restoreGlobalDialogOverrides(): void {
    if (
      typeof HTMLDialogElement === 'undefined' ||
      !originalDialogShowModal ||
      !isDialogShowModalPatched
    ) {
      return;
    }

    HTMLDialogElement.prototype.showModal = originalDialogShowModal;
    isDialogShowModalPatched = false;
  }

  /**
   * Hide all existing theme cart dialogs
   */
  protected hideExistingThemeCartDialogs(): void {
    const themeCartDialogs = document.querySelectorAll('dialog.cart-drawer__dialog');

    themeCartDialogs.forEach((dialog) => {
      if (dialog instanceof HTMLDialogElement) {
        try {
          if (dialog.open) {
            dialog.close();
          }
        } catch (e) {
          // Ignore close errors
          console.log('Failed to close theme cart dialog:', e);
        }

        dialog.removeAttribute('open');
        dialog.style.setProperty('display', 'none', 'important');
      }
    });

    // Run again after a short delay to catch any dialogs that might be added by theme
    setTimeout(() => {
      const newThemeCartDialogs = document.querySelectorAll('dialog.cart-drawer__dialog');
      newThemeCartDialogs.forEach((dialog) => {
        if (dialog instanceof HTMLDialogElement) {
          try {
            if (dialog.open) {
              dialog.close();
            }
          } catch (e) {
            console.log('Failed to close theme cart dialog:', e);
          }

          dialog.removeAttribute('open');
          dialog.style.setProperty('display', 'none', 'important');
        }
      });
    }, 50);
  }

  /**
   * Setup periodic monitoring to force close any theme cart dialogs in top-layer
   */
  protected setupTopLayerMonitoring(): void {
    if (this.topLayerMonitoringInterval) {
      return;
    }

    this.topLayerMonitoringInterval = window.setInterval(() => {
      const modalDialogs = document.querySelectorAll('dialog:modal');

      modalDialogs.forEach((dialog) => {
        if (dialog instanceof HTMLDialogElement) {
          const isThemeCartDialog = dialog.classList.contains('cart-drawer__dialog');

          if (isThemeCartDialog) {
            try {
              dialog.close();
            } catch (e) {
              console.log('Failed to close theme cart dialog:', e);
            }

            dialog.style.setProperty('display', 'none', 'important');
            dialog.removeAttribute('open');
          }
        }
      });
    }, 100);
  }

  /**
   * Setup cart icon click handler and accessibility
   * Override to customize cart icon behavior for specific themes
   */
  protected setupCartIcon(): void {
    try {
      const matchedConfig = this.cartSelectors.getCartSelectorAndUpdateAction();

      if (!matchedConfig?.selector) {
        this.appService.logger.error('Cart icon selector not found in matched config');
        return;
      }

      const cartIcon = document.querySelector(matchedConfig.selector);

      if (!cartIcon) {
        this.appService.logger.error(
          'Cart icon element not found with selector:',
          matchedConfig.selector
        );
        return;
      }

      // Remove any existing event listeners by cloning the element
      const newCartIcon = cartIcon.cloneNode(true);
      cartIcon.parentNode?.replaceChild(newCartIcon, cartIcon);

      // If cart icon is an anchor tag, remove href and add proper attributes
      if (newCartIcon instanceof HTMLAnchorElement) {
        const anchor = newCartIcon;
        anchor.setAttribute('role', 'button');
        anchor.setAttribute('tabindex', '0');
      }

      // Add click handler with event prevention
      newCartIcon.addEventListener(
        'click',
        async (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Hide theme cart dialogs immediately
          this.hideExistingThemeCartDialogs();

          // Hide again after a very short delay to catch any theme logic that might run
          setTimeout(() => {
            this.hideExistingThemeCartDialogs();
          }, 10);

          this.toggleCart();
          return false;
        },
        { capture: true }
      );

      // Add keyboard event listeners for accessibility
      newCartIcon.addEventListener('keydown', async (e: Event) => {
        if (!(e instanceof KeyboardEvent)) return;
        const keyEvent = e;
        if (this.SUBMIT_KEYS.includes(keyEvent.key)) {
          keyEvent.preventDefault();
          keyEvent.stopPropagation();

          // Hide theme cart dialogs for keyboard navigation too
          this.hideExistingThemeCartDialogs();
          setTimeout(() => this.hideExistingThemeCartDialogs(), 10);

          window.focusedElementBeforeModalCart = this.domHelper.validateHTMLElement(
            document.activeElement
          );
          this.openCart();
        }
      });
    } catch (error) {
      this.appService.logger.error('Failed to setup cart icon:', error);
    }
  }

  /**
   * Setup global event listeners for cart operations
   * Override to add custom event handlers for specific shops
   */
  protected setupEventListeners(): void {
    // Listen to custom events
    this.addEventListener(window, 'boost-sd-open-cart', this.openCartEventHandler);
    this.addEventListener(window, 'boost-sd-close-cart', this.closeCartEventHandler);
    this.addEventListener(window, 'boost-sd-re-render-cart', this.rerenderCartEventHandler);
  }

  /**
   * Toggle cart modal visibility
   * Public method to show/hide cart programmatically
   *
   * @example
   * ```javascript
   * // Get cart controller instance
   * const cartController = window.boostWidgetIntegration.get('CartController');
   * await cartController.toggleCart();
   * ```
   */
  toggleCart() {
    const cart = this.state.value;
    if (cart.isShow) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  /**
   * Should open cart modal after add to cart operation
   * Protected method - override to customize cart opening behavior for specific shops
   *
   * @param params - Product parameters used for add to cart
   * @param result - Result from cart service add operation
   * @returns Whether to open cart modal
   * @example
   * ```typescript
   * // Don't open cart for certain product types
   * protected shouldOpenCartAfterAdd(params: unknown, result: any): boolean {
   *   const productType = (params as any)?.productType;
   *   if (productType === 'gift-card') {
   *     return false; // Don't open cart for gift cards
   *   }
   *   return super.shouldOpenCartAfterAdd(params, result);
   * }
   *
   * // Disable cart for specific shops
   * protected shouldOpenCartAfterAdd(): boolean {
   *   const shopDomain = window.Shopify?.shop;
   *   if (shopDomain === 'custom-shop.myshopify.com') {
   *     return false; // Never auto-open cart for this shop
   *   }
   *   return super.shouldOpenCartAfterAdd();
   * }
   * ```
   */
  protected shouldOpenCartAfterAdd(params: unknown, result: unknown): boolean {
    const { enableCart, autoOpenAjaxCartOnAddToCart = true } = this.appService.themeSettings.cart;

    // Check for noneCartOption flag (global cart disable option)
    const { noneCartOption = false } =
      (window as Window & { boostWidgetIntegration?: { noneCartOption?: boolean } })
        .boostWidgetIntegration ?? {};

    return Boolean(enableCart && autoOpenAjaxCartOnAddToCart && !noneCartOption);
  }

  /**
   * Add product to cart with optional cart modal display
   *
   * @param params - Product parameters (variant ID, quantity, properties, etc.)
   * @param button - Optional button element to show loading states
   * @param shouldOpenCart - Whether to open cart modal after adding (default: true)
   * @param callback - Optional callback to execute after operation
   *
   * @example
   * ```javascript
   * // Add to cart and show modal
   * await cartController.addToCart({ id: '12345', quantity: 2 });
   *
   * // Add to cart without showing modal
   * await cartController.addToCart({ id: '12345' }, null, false);
   *
   * // Add with button loading state
   * const button = document.querySelector('.add-to-cart-btn');
   * await cartController.addToCart({ id: '12345' }, button, true, () => {
   *   console.log('Added to cart!');
   * });
   * ```
   */
  public async addToCart(
    params: Record<string, unknown>,
    button: HTMLElement | null = null,
    shouldOpenCart = true,
    callback?: () => void
  ): Promise<AddToCartResult | undefined> {
    try {
      // Update button state to "Adding..."
      if (button) {
        this.updateButtonState(button, 'adding');
      }

      // Call CartService to add to cart
      const result = await this.cartService.addToCart(params);

      if (result.success) {
        // Update button state to "Added!" and remove disabled immediately (like legacy cart)
        if (button) {
          this.updateButtonState(button, 'added');

          // Reset button text after delay
          setTimeout(() => {
            this.updateButtonState(button, 'default');
          }, this.getButtonSuccessResetDelay());
        }

        // Open cart based on configuration and shouldOpenCart parameter
        if (shouldOpenCart && this.shouldOpenCartAfterAdd(params, result)) {
          this.openCart();
        } else if (!shouldOpenCart) {
          // If explicitly requested not to open cart, just re-render to update count
          await this.render();
        }

        if (callback) {
          callback();
        }
      } else {
        this.appService.logger.error('Add to cart failed:', result.error);

        if (button) {
          this.updateButtonState(button, 'failed');

          // Reset to original text after delay
          setTimeout(() => {
            this.updateButtonState(button, 'default');
          }, this.getButtonFailureResetDelay());
        }

        if (callback) {
          setTimeout(callback, 300);
        }
      }
      return result;
    } catch (error) {
      this.appService.logger.error('Error in addToCart:', error);

      // Update button to show "Failed!" and reset after delay
      if (button) {
        this.updateButtonState(button, 'failed');

        // Reset to original text after delay
        setTimeout(() => {
          this.updateButtonState(button, 'default');
        }, this.getButtonFailureResetDelay());
      }

      // Call callback if provided (onFail callback)
      if (callback) {
        setTimeout(callback, this.getCallbackDelay('failure'));
      }
      return undefined;
    }
  }

  /**
   * Get callback delay for add to cart operations
   * Protected method - override to customize callback timing
   *
   * @param type - Type of callback ('success' | 'failure')
   * @returns Delay in milliseconds
   * @example
   * ```typescript
   * // Faster callbacks for responsive UX
   * protected getCallbackDelay(type: 'success' | 'failure'): number {
   *   return type === 'success' ? 0 : 200;
   * }
   * ```
   */
  protected getCallbackDelay(type: 'success' | 'failure'): number {
    return type === 'success' ? 0 : 300;
  }

  /**
   * Open cart modal
   * Public method to show cart programmatically
   *
   * @example
   * ```javascript
   * const cartController = window.boostWidgetIntegration.get('CartController');
   * cartController.openCart();
   * ```
   */
  openCart() {
    // Ensure theme cart dialogs are hidden before opening app cart
    this.hideExistingThemeCartDialogs();

    if (!this.state.value.isShow) {
      this.state.value.isShow = true;

      this.saveFocusElementBeforeCart();
    }

    this.render();
  }

  /**
   * Save focused element before opening cart modal
   * Protected method - override to customize focus management for specific themes
   *
   * @example
   * ```typescript
   * // Custom focus saving for specific theme
   * protected saveFocusElementBeforeCart(): void {
   *   super.saveFocusElementBeforeCart();
   *   // Additional custom focus management
   *   this.customFocusElement = document.querySelector('.custom-focus');
   * }
   * ```
   */
  protected saveFocusElementBeforeCart(): void {
    if (this.focusedElementBeforeMiniCart === null) {
      window.focusedElementBeforeModalCart = this.domHelper.validateHTMLElement(
        document.activeElement
      );
    }
  }

  /**
   * Hide cart modal
   * Public method to close cart programmatically
   *
   * @example
   * ```javascript
   * const cartController = window.boostWidgetIntegration.get('CartController');
   * cartController.hideCart();
   * ```
   */
  public closeCart(): void {
    this.state.value.isShow = false;
    this.cleanupCartModal();

    // Restore focus for keyboard accessibility
    this.restoreFocusAfterCart();

    // Dispatch event for cart drawer recommendation widgets cleanup
    window.dispatchEvent(new CustomEvent('boost-sd-cart-drawer-closed'));
  }

  override destroy(): void {
    if (this.state.value.isShow) {
      this.closeCart();
    } else {
      this.cleanupCartModal();
    }

    if (this.topLayerMonitoringInterval) {
      clearInterval(this.topLayerMonitoringInterval);
      this.topLayerMonitoringInterval = null;
    }

    this.restoreGlobalDialogOverrides();
    document.body.classList.remove(this.getCartEnabledBodyClass());
    document.body.classList.remove(this.getModalOpenBodyClass());

    this.closeButtonHandler = null;
    this.viewCartHandler = null;
    this.checkoutHandler = null;
    this.outsideClickHandler = null;
    this.focusedElementBeforeMiniCart = null;
    window.focusedElementBeforeModalCart = null;

    this.state.value.isShow = false;
    this.state.value.isInitialized = false;
    this.state.value.isRendering = false;

    super.destroy();
  }

  /**
   * Restore focused element after closing cart modal
   * Protected method - override to customize focus restoration for specific themes
   *
   * @example
   * ```typescript
   * // Custom focus restoration
   * protected restoreFocusAfterCart(): void {
   *   super.restoreFocusAfterCart();
   *   // Additional custom focus restoration
   *   if (this.customFocusElement) {
   *     this.customFocusElement.focus();
   *   }
   * }
   * ```
   */
  protected restoreFocusAfterCart(): void {
    // Only return focus if the interaction was via keyboard
    if (this.wcagHelper.isKeyboardActivated()) {
      if (this.focusedElementBeforeMiniCart) {
        this.focusedElementBeforeMiniCart.focus();
        this.focusedElementBeforeMiniCart = null;
      } else if (window.buttonQuickviewFocus) {
        window.buttonQuickviewFocus.focus();
        window.buttonQuickviewFocus = null;
      } else if (window.focusedElementBeforeModalCart) {
        window.focusedElementBeforeModalCart.focus();
        window.focusedElementBeforeModalCart = null;
      }
    }
  }

  /**
   * Validate numeric input (allows only digits)
   * Protected method - override to customize input validation
   *
   * @param evt - Keyboard event
   */
  protected onlyNumbers(evt: KeyboardEvent): void {
    const charCode = evt?.which || evt?.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      evt.preventDefault();
    }
  }

  /**
   * Save focused element before cart modal interaction
   * Protected wrapper for WCAG utility
   *
   * @param element - Element to save focus for
   */
  protected saveFocusElement(element: HTMLElement): void {
    this.wcagHelper.saveFocusElement(element);
  }

  /**
   * Restore previously focused element after cart modal closes
   * Protected wrapper for WCAG utility
   */
  protected restoreFocusedElement(): void {
    this.wcagHelper.restoreFocusedElement();
  }

  /**
   * Get WCAG focusable element selectors
   * Override to customize focusable elements for specific themes
   *
   * @returns CSS selector string
   */
  protected getWCAGFocusableSelectors(): string {
    return 'a[href], button:not([disabled]), input';
  }

  /**
   * Get modal close button selector for WCAG
   * Override to use different close button selector
   *
   * @returns CSS selector string
   */
  protected getModalCloseButtonSelector(): string {
    return '.boost-sd__modal-close-btn';
  }

  /**
   * Setup WCAG accessibility features for cart modal
   * Protected method - override to customize accessibility behavior
   */
  protected setupWCAGAccessibility(): void {
    const modal = document.getElementById(this.getModalId());
    if (!modal) return;

    // Focus first interactive element in modal
    this.wcagHelper.focusFirstElement(modal, this.getWCAGFocusableSelectors());

    // Trap focus within modal (prevent Tab from escaping modal)
    const handleTrapFocus = (e: Event) => {
      if (e instanceof KeyboardEvent) {
        this.wcagHelper.trapFocus(
          e,
          modal,
          this.getWCAGFocusableSelectors(),
          this.getModalCloseButtonSelector()
        );
      }
    };

    modal.addEventListener('keydown', handleTrapFocus);

    // Store the handler so we can remove it later
    this.trapFocusHandlers.set(modal, handleTrapFocus);
  }

  /**
   * Cleanup WCAG event listeners
   * Protected method - override to customize cleanup behavior
   */
  protected cleanupWCAGAccessibility(): void {
    const modal = document.getElementById(this.getModalId());
    if (!modal) return;

    const handleTrapFocus = this.trapFocusHandlers.get(modal);
    if (handleTrapFocus) {
      modal.removeEventListener('keydown', handleTrapFocus);
      this.trapFocusHandlers.delete(modal);
    }
  }

  /**
   * Handle clicks outside modal to close cart
   * Protected method - override to customize outside-click behavior
   *
   * @param element - Modal content element
   * @param event - Click event
   * @param callback - Callback to execute on outside click
   * @param ignore - Element to ignore clicks on (e.g., close button)
   */
  protected outsideClickListener(
    element: Element | null,
    event: Event,
    callback: (event: Event) => void,
    ignore?: Element | null
  ): void {
    const target = event.target;
    if (target instanceof Element && !element?.contains(target) && !ignore?.contains(target)) {
      callback(event);
    }
  }

  /**
   * Set window location (navigate to URL)
   * Protected method - override to customize navigation behavior
   *
   * Security: Only accepts relative URLs (same-origin) to prevent XSS attacks
   *
   * @param url - Relative URL to navigate to (must start with /)
   */
  protected setWindowLocation(url: string): void {
    // XSS Protection: Only allow relative URLs starting with /
    if (!url.startsWith('/')) {
      this.appService.logger.error('Invalid navigation URL - must be relative path:', url);
      return;
    }

    // Prevent protocol-relative URLs (//evil.com)
    if (url.startsWith('//')) {
      this.appService.logger.error(
        'Invalid navigation URL - protocol-relative URLs not allowed:',
        url
      );
      return;
    }

    // Safe to navigate - URL is relative same-origin path
    window.location.href = url;
  }

  /**
   * Build URL with localization prefix
   * Protected method - override to customize URL building for specific shops
   *
   * @param path - Path without locale prefix (e.g., 'cart', 'products/123')
   * @returns Full URL with locale prefix if applicable
   * @example
   * ```typescript
   * // For English shop: buildUrlWithLocalization('cart') => '/cart'
   * // For French shop: buildUrlWithLocalization('cart') => '/fr/cart'
   * ```
   */
  protected buildUrlWithLocalization(path: string): string {
    const currentLocale = (window?.Shopify?.routes?.root || '/').replace(/\/$/, '');
    return currentLocale === '' ? `/${path}` : `${currentLocale}/${path}`;
  }

  /**
   * Get cart item selector
   * Override to use different cart item selector for custom themes
   *
   * @returns CSS selector string
   */
  protected getCartItemSelector(): string {
    return '.boost-sd__cart-item';
  }

  /**
   * Get cart item button selectors
   * Override to use different button selectors for custom themes
   *
   * @returns Object with selector strings for cart item buttons
   */
  protected getCartItemButtonSelectors(): {
    quantityButtons: string;
    quantityInput: string;
    removeButton: string;
  } {
    return {
      quantityButtons: '.boost-sd__input-number-btn',
      quantityInput: '.boost-sd__input-number-input',
      removeButton: '.boost-sd__cart-item-remove-btn',
    };
  }

  /**
   * Setup event listeners for cart items (quantity, remove, etc.)
   * Protected method - override to customize cart item interaction behavior
   *
   * @param modal - Optional modal element to scope event listeners
   */
  protected setupCartItemEvents(modal?: HTMLElement): void {
    const cartItems = (modal || document).querySelectorAll(this.getCartItemSelector());
    const selectors = this.getCartItemButtonSelectors();

    cartItems?.forEach((cartItem) => {
      const buttons = cartItem.querySelectorAll(selectors.quantityButtons);
      const decreaseQuantityBtn = this.domHelper.validateHTMLElement(buttons?.[0]);
      const increaseQuantityBtn = this.domHelper.validateHTMLElement(buttons?.[1]);
      const quantityInput = this.domHelper.validateHTMLElement(
        cartItem.querySelector(selectors.quantityInput)
      );
      const removeItemBtn = this.domHelper.validateHTMLElement(
        cartItem.querySelector(selectors.removeButton)
      );
      const productId = cartItem.getAttribute('data-product-id');

      if (!productId) return;

      // Decrease quantity
      decreaseQuantityBtn?.addEventListener('click', async () => {
        // Prevent multiple clicks while updating
        if (decreaseQuantityBtn.hasAttribute('disabled')) {
          return;
        }

        decreaseQuantityBtn.setAttribute('disabled', 'true');
        increaseQuantityBtn?.setAttribute('disabled', 'true');

        this.saveFocusElement(decreaseQuantityBtn);

        try {
          if (quantityInput instanceof HTMLInputElement) {
            await this.cartService.changeQuantity(productId, Number(quantityInput.value) - 1);
          }
        } catch (error) {
          this.appService.logger.error('Error decreasing quantity:', error);
          // Re-enable buttons on error
          decreaseQuantityBtn.removeAttribute('disabled');
          increaseQuantityBtn?.removeAttribute('disabled');
        } finally {
          // Always reload cart to show current state
          this.render();
          this.restoreFocusedElement();
        }
      });

      // Increase quantity
      increaseQuantityBtn?.addEventListener('click', async () => {
        // Prevent multiple clicks while updating
        if (increaseQuantityBtn.hasAttribute('disabled')) {
          return;
        }

        decreaseQuantityBtn?.setAttribute('disabled', 'true');
        increaseQuantityBtn.setAttribute('disabled', 'true');

        this.saveFocusElement(increaseQuantityBtn);

        try {
          if (quantityInput instanceof HTMLInputElement) {
            await this.cartService.changeQuantity(productId, Number(quantityInput.value) + 1);
          }
        } catch (error) {
          this.appService.logger.error('Error increasing quantity:', error);
          // Re-enable buttons on error
          decreaseQuantityBtn?.removeAttribute('disabled');
          increaseQuantityBtn.removeAttribute('disabled');
        } finally {
          // Always reload cart to show current state
          this.render();
          this.restoreFocusedElement();
        }
      });

      // Remove item
      removeItemBtn?.addEventListener('click', async () => {
        // Prevent multiple clicks while removing
        if (removeItemBtn.hasAttribute('disabled')) {
          return;
        }

        removeItemBtn.setAttribute('disabled', 'true');

        this.saveFocusElement(removeItemBtn);

        try {
          await this.cartService.changeQuantity(productId, 0);
          this.render();
          this.restoreFocusedElement();
        } catch (error) {
          this.appService.logger.error('Error removing item:', error);
          // Re-enable button on error
          removeItemBtn.removeAttribute('disabled');
        }
      });

      // Quantity input validation - prevent non-numeric input
      quantityInput?.addEventListener('keypress', (e) => {
        this.onlyNumbers(e);
      });

      // Prevent paste of non-numeric content
      quantityInput?.addEventListener('paste', (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData?.getData('text');
        if (pastedText && /^\d+$/.test(pastedText)) {
          // Only paste if it's all digits
          const input = e.target;
          if (input instanceof HTMLInputElement) {
            input.value = pastedText;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });

      // Sanitize input value in real-time (handles paste, auto-fill, etc.)
      quantityInput?.addEventListener('input', (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement)) return;
        // Remove any non-digit characters
        input.value = input.value.replace(/\D/g, '');

        // Prevent leading zeros (but allow empty for editing)
        if (input.value.length > 1 && input.value.startsWith('0')) {
          input.value = input.value.replace(/^0+/, '');
        }
      });

      // Handle blur/change event to update cart
      quantityInput?.addEventListener('change', async (e) => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement)) return;
        const value = target.value.replace(/\D/g, ''); // Sanitize again

        this.saveFocusElement(quantityInput);

        // Validate: must be positive integer
        const numValue = parseInt(value, 10);
        try {
          if (!isNaN(numValue) && numValue > 0) {
            await this.cartService.changeQuantity(productId, numValue);
          } else {
            // Reset to previous valid value (1 as fallback)
            target.value = '1';
            await this.cartService.changeQuantity(productId, 1);
          }
        } catch (error) {
          // On error (e.g., 422 for invalid quantity), just log it
          this.appService.logger.error('Failed to change quantity:', error);
        } finally {
          // Always reload cart to show current state
          this.render();
          this.restoreFocusedElement();
        }
      });
    });
  }

  /**
   * Get cart action button selectors
   * Override to use different button selectors for custom themes
   *
   * @returns Object with selector strings for cart action buttons
   */
  protected getCartActionButtonSelectors(): {
    viewCart: string;
    checkout: string;
    close: string;
  } {
    return {
      viewCart: '.boost-sd__btn-view-cart',
      checkout: '.boost-sd__btn-checkout',
      close: '.boost-sd__modal-close-btn',
    };
  }

  /**
   * Get cart content selector for outside-click detection
   * Override to use different cart content selector
   *
   * @returns CSS selector string
   */
  protected getCartContentSelector(): string {
    return '.boost-sd__cart';
  }

  /**
   * Setup event listeners for cart action buttons (view cart, checkout, close)
   * Protected method - override to customize button behavior
   */
  protected setupCartButtonEvents(): void {
    const modal = document.getElementById(this.getModalId());

    if (!modal) {
      return;
    }

    const selectors = this.getCartActionButtonSelectors();
    const viewCartBtn = modal.querySelector(selectors.viewCart);
    const checkOutBtn = modal.querySelector(selectors.checkout);
    const closeBtn = modal.querySelector(selectors.close);

    // Remove existing handlers first
    if (this.viewCartHandler && viewCartBtn) {
      viewCartBtn.removeEventListener('click', this.viewCartHandler);
    }
    if (this.checkoutHandler && checkOutBtn) {
      checkOutBtn.removeEventListener('click', this.checkoutHandler);
    }
    if (this.closeButtonHandler && closeBtn) {
      closeBtn.removeEventListener('click', this.closeButtonHandler);
    }

    // Setup view cart button if it exists
    if (viewCartBtn) {
      this.viewCartHandler = () => {
        this.setWindowLocation(this.buildUrlWithLocalization(this.getCartPagePath()));
      };
      viewCartBtn.addEventListener('click', this.viewCartHandler);
    }

    // Setup checkout button if it exists
    if (checkOutBtn) {
      this.checkoutHandler = () => {
        this.setWindowLocation(this.getCheckoutUrl());
      };
      checkOutBtn.addEventListener('click', this.checkoutHandler);
    }

    // Setup close button if it exists
    if (closeBtn) {
      this.closeButtonHandler = () => {
        this.closeCart();
      };
      closeBtn.addEventListener('click', this.closeButtonHandler);
    }
  }

  /**
   * Setup click-outside listener to close modal
   * Protected method - override to customize click-outside behavior
   */
  protected setupModalClickOutside(): void {
    const modal = document.getElementById(this.getModalId());
    if (modal) {
      modal.addEventListener('click', (e) => {
        this.outsideClickListener(
          document.querySelector(this.getCartContentSelector()),
          e,
          this.closeCart,
          document.querySelector(this.getModalCloseButtonSelector())
        );
      });
    }
  }

  /**
   * Cleanup cart modal and event listeners
   * Protected method - override to customize cleanup behavior
   */
  protected cleanupCartModal(): void {
    const modal = document.getElementById(this.getModalId());
    const selectors = this.getCartActionButtonSelectors();
    const closeBtn = document.querySelector(selectors.close);
    const viewCartBtn = document.querySelector(selectors.viewCart);
    const checkOutBtn = document.querySelector(selectors.checkout);

    // Remove event listeners using stored handler references
    if (this.closeButtonHandler && closeBtn) {
      closeBtn.removeEventListener('click', this.closeButtonHandler);
    }
    if (this.viewCartHandler && viewCartBtn) {
      viewCartBtn.removeEventListener('click', this.viewCartHandler);
    }
    if (this.checkoutHandler && checkOutBtn) {
      checkOutBtn.removeEventListener('click', this.checkoutHandler);
    }

    modal?.removeEventListener('click', () => this.setupModalClickOutside());
    if (this.outsideClickHandler && modal) {
      modal.removeEventListener('click', this.outsideClickHandler);
    }
    modal?.remove();
    document.body.classList.remove(this.getModalOpenBodyClass());

    // Clean up body styles added by theme cart
    this.cleanupBodyStyles();
  }

  /**
   * Clean up body styles added by theme cart
   * Protected method - override to customize body style cleanup
   */
  protected cleanupBodyStyles(): void {
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
  }

  /**
   * Handle context render - main render logic
   * Protected method - override to customize render pipeline
   */
  async render(): Promise<void> {
    const stateValue = this.state.value;
    if (stateValue.isRendering) {
      return;
    }

    stateValue.isRendering = true;

    try {
      const { html, itemCount } = await this.cartService.renderCartModal();

      // Update cart count in UI
      const item = this.cartSelectors.getCartSelectorAndUpdateAction();
      item?.action(itemCount);

      this.outsideClickHandler = (e: Event) => {
        const modal = document.getElementById(this.getModalId());
        const closeBtn = modal?.querySelector(this.getModalCloseButtonSelector());
        const cb = () => {
          this.closeCart();
        };

        this.outsideClickListener(
          document.querySelector(this.getCartContentSelector()),
          e,
          cb,
          closeBtn
        );
      };

      const modal = document.getElementById(this.getModalId());

      if (stateValue.isShow) {
        // Remove existing modal
        if (modal) {
          document.body.removeChild(modal);
        }

        document.body.insertAdjacentHTML('beforeend', html);
        document.body.classList.add(this.getModalOpenBodyClass());

        setTimeout(() => {
          this.setupCartItemEvents();
          this.setupCartButtonEvents();

          if (this.outsideClickHandler) {
            document
              .getElementById(this.getModalId())
              ?.addEventListener('click', this.outsideClickHandler);
          }

          this.setupWCAGAccessibility();

          // Dispatch event for cart drawer recommendation widgets after DOM is ready
          window.dispatchEvent(new CustomEvent('boost-sd-cart-drawer-opened'));
        }, 0);
      } else {
        // Clean up when cart is closed
        const selectors = this.getCartActionButtonSelectors();
        const closeBtn = document.querySelector(selectors.close);
        const viewCartBtn = document.querySelector(selectors.viewCart);
        const checkOutBtn = document.querySelector(selectors.checkout);

        // Remove event listeners using stored handler references
        if (this.closeButtonHandler && closeBtn) {
          closeBtn.removeEventListener('click', this.closeButtonHandler);
        }
        if (this.viewCartHandler && viewCartBtn) {
          viewCartBtn.removeEventListener('click', this.viewCartHandler);
        }
        if (this.checkoutHandler && checkOutBtn) {
          checkOutBtn.removeEventListener('click', this.checkoutHandler);
        }

        this.cleanupWCAGAccessibility();

        if (this.outsideClickHandler) {
          document
            .getElementById(this.getModalId())
            ?.removeEventListener('click', this.outsideClickHandler);
        }

        document.getElementById(this.getModalId())?.remove();
        document.body.classList.remove(this.getModalOpenBodyClass());
      }
    } catch (error) {
      this.appService.logger.error('Error in context render:', error);
    } finally {
      stateValue.isRendering = false;
    }
  }
}
